/**
 * Regulatory framework definitions — DATA, not branching code.
 *
 * The review report is the only artefact an external signatory sees, and it used to show
 * every tenant the same fixed requirement set, framed as "the six key questions required
 * under the applicable regulatory framework" — deliberately vague, because the items came
 * from Swiss VQF practice. A Lithuanian EMI's reviewer was therefore asked to sign off
 * against Form A / Form K and a professional-treasury test that have no application to
 * them, while never seeing EU AMLD or their own national implementation named.
 *
 * Everything here is keyed off firms.compliance_config.regulators (an ARRAY — a firm can
 * be supervised under more than one framework, and Daw Global is EU AMLD plus a Lithuanian
 * addendum). Requirements are the UNION across selected frameworks: if any framework
 * applicable to the firm requires an instrument, it is shown.
 *
 * Deliberately kept as one code path. Adding a framework means adding an entry here, not
 * a new report component — per-tenant report variants would be code paths nobody can see
 * when a bug is reported.
 */

// Instruments are framework-specific requirements that must NOT appear for firms the
// framework does not apply to.
export const INSTRUMENTS = {
  FORM_AK: 'form_ak',                       // Swiss Form A / Form K beneficial-owner declarations
  ONLINE_IDENTIFICATION: 'online_id',       // FINMA Circular 2016/7 confirmation payment / video / NFC
  VQF_PROFILES: 'vqf_profiles',             // VQF 902.4 risk profile, 902.5 customer profile
  PROFESSIONAL_TREASURY: 'prof_treasury',   // Swiss professional-treasury / qualified-investor concept
  TRAVEL_RULE: 'travel_rule'                // Crypto transfer originator/beneficiary information
};

export const FRAMEWORKS = {
  fca_uk: {
    label: 'FCA — United Kingdom',
    short: 'FCA / MLR 2017',
    // The requirement-set citation shown at the head of the MLRO summary.
    citation: 'the Money Laundering, Terrorist Financing and Transfer of Funds (Information on the Payer) Regulations 2017',
    requirementCitations: {
      identity: 'MLR 2017 Reg 28(2) — identification and verification of the customer',
      beneficialOwners: 'MLR 2017 Reg 28(4) — identification of beneficial owners',
      businessActivity: 'MLR 2017 Reg 28(11) — nature and purpose of the business relationship',
      sourceOfWealth: 'MLR 2017 Reg 33 — enhanced due diligence, source of funds and wealth',
      authority: 'MLR 2017 Reg 28(10) — person purporting to act on behalf of the customer',
      taxCompliance: 'Criminal Finances Act 2017 — corporate failure to prevent facilitation of tax evasion'
    },
    instruments: [INSTRUMENTS.TRAVEL_RULE]
  },

  ch_vqf_finma: {
    label: 'VQF / FINMA — Switzerland',
    short: 'VQF / AMLA',
    citation: 'the Anti-Money Laundering Act (AMLA), AMLO-FINMA and the VQF SRO Regulations',
    requirementCitations: {
      identity: 'Art. 3 AMLA — identification of the contracting party',
      beneficialOwners: 'Art. 4 AMLA and Art. 2a — establishing the beneficial owner',
      businessActivity: 'Art. 6 AMLA: nature and purpose of the business relationship',
      sourceOfWealth: 'Art. 6 AMLA — plausibility of the origin of assets',
      authority: 'Art. 3 AMLA — authority of the establishing person',
      taxCompliance: 'VQF SRO Regulations — tax compliance declaration',
      formAK: 'Form A (beneficial owner) / Form K (controlling person) — VQF SRO Regulations',
      onlineIdentification: 'FINMA Circular 2016/7 — online identification of the contracting party',
      professionalTreasury: 'Art. 2 para. 2 AMLA — professionally held treasury / prudentially supervised counterparty'
    },
    instruments: [
      INSTRUMENTS.FORM_AK,
      INSTRUMENTS.ONLINE_IDENTIFICATION,
      INSTRUMENTS.VQF_PROFILES,
      INSTRUMENTS.PROFESSIONAL_TREASURY,
      INSTRUMENTS.TRAVEL_RULE
    ]
  },

  eu_amld: {
    label: 'EU — AMLD / AMLA',
    short: 'EU AMLD',
    citation: 'the EU Anti-Money Laundering Directives as implemented in national law, and Regulation (EU) 2023/1113',
    requirementCitations: {
      identity: 'AMLD Art. 13(1)(a) — customer identification and verification',
      beneficialOwners: 'AMLD Art. 13(1)(b) and Art. 3(6) — beneficial ownership',
      businessActivity: 'AMLD Art. 13(1)(c) — purpose and intended nature of the business relationship',
      sourceOfWealth: 'AMLD Art. 18 and Art. 18a — enhanced due diligence, source of funds and wealth',
      authority: 'AMLD Art. 13(1)(a) — persons purporting to act on behalf of the customer',
      taxCompliance: 'National implementing law — tax compliance and predicate offences'
    },
    instruments: [INSTRUMENTS.TRAVEL_RULE]
  },

  de_bafin: {
    label: 'BaFin — Germany',
    short: 'GwG / BaFin',
    citation: 'the Geldwäschegesetz (GwG) and BaFin guidance',
    requirementCitations: {
      identity: '§ 10-11 GwG — customer identification',
      beneficialOwners: '§ 3 and § 11 GwG — beneficial ownership, Transparenzregister',
      businessActivity: '§ 10(1) GwG — purpose and nature of the business relationship',
      sourceOfWealth: '§ 15 GwG — enhanced due diligence, origin of assets',
      authority: '§ 11(4) GwG — persons acting on behalf of the customer',
      taxCompliance: 'GwG predicate offences — tax evasion'
    },
    instruments: [INSTRUMENTS.TRAVEL_RULE]
  },

  sg_mas: {
    label: 'MAS — Singapore',
    short: 'MAS Notices',
    citation: 'the MAS Notices on prevention of money laundering and countering the financing of terrorism',
    requirementCitations: {
      identity: 'MAS Notice — customer due diligence and identification',
      beneficialOwners: 'MAS Notice — identification of beneficial owners and connected parties',
      businessActivity: 'MAS Notice — purpose and intended nature of the account relationship',
      sourceOfWealth: 'MAS Notice — enhanced CDD, source of wealth and funds',
      authority: 'MAS Notice — natural persons appointed to act on the customer\u2019s behalf',
      taxCompliance: 'MAS Notice — tax crimes as predicate offences'
    },
    instruments: [INSTRUMENTS.TRAVEL_RULE]
  },

  us_fincen: {
    label: 'FinCEN / OFAC — United States',
    short: 'BSA / FinCEN',
    citation: 'the Bank Secrecy Act, the FinCEN Customer Due Diligence Rule and OFAC sanctions requirements',
    requirementCitations: {
      identity: 'BSA Customer Identification Program (CIP)',
      beneficialOwners: 'FinCEN CDD Rule 31 CFR 1010.230 — beneficial ownership of legal entity customers',
      businessActivity: 'FinCEN CDD Rule — customer risk profile, nature and purpose',
      sourceOfWealth: 'BSA/AML enhanced due diligence — source of funds and wealth',
      authority: 'CIP — persons opening the account on behalf of the entity',
      taxCompliance: 'BSA predicate offences — tax evasion'
    },
    instruments: [INSTRUMENTS.TRAVEL_RULE]
  },

  ae_vara: {
    label: 'VARA / DFSA — UAE',
    short: 'UAE AML',
    citation: 'UAE Federal Decree-Law 20/2018, Cabinet Decision 10/2019 and the rules of the relevant authority (VARA, DFSA or FSRA)',
    requirementCitations: {
      identity: 'Cabinet Decision 10/2019 — customer identification',
      beneficialOwners: 'Cabinet Decision 10/2019 — beneficial owners and controllers',
      businessActivity: 'Cabinet Decision 10/2019 — purpose and nature of the relationship',
      sourceOfWealth: 'Cabinet Decision 10/2019 — enhanced due diligence, source of funds and wealth',
      authority: 'Cabinet Decision 10/2019 — representatives acting for the customer',
      taxCompliance: 'UAE AML framework — predicate offences'
    },
    instruments: [INSTRUMENTS.TRAVEL_RULE]
  }
};

/**
 * Resolve a firm's selected regulators into a single reporting context.
 * Unknown or absent selections fall back to a framework-neutral presentation, which is
 * what every tenant saw before this existed — so a firm with no regulator configured is
 * no worse off than it was.
 */
export function frameworkContext(complianceConfig) {
  const cfg = complianceConfig || {};
  const selected = Array.isArray(cfg.regulators) ? cfg.regulators.filter(r => FRAMEWORKS[r]) : [];
  const frameworks = selected.map(r => FRAMEWORKS[r]);
  const other = (cfg.otherRegulator || '').trim();

  const instruments = new Set();
  frameworks.forEach(f => (f.instruments || []).forEach(i => instruments.add(i)));
  // No framework configured: show everything rather than silently hiding a requirement a
  // firm may rely on. Under-showing is the dangerous direction.
  const unconfigured = frameworks.length === 0 && !other;
  if (unconfigured) Object.values(INSTRUMENTS).forEach(i => instruments.add(i));

  const labels = [...frameworks.map(f => f.label), ...(other ? [other] : [])];
  const shorts = [...frameworks.map(f => f.short), ...(other ? [other] : [])];

  const citation = frameworks.length
    ? frameworks.map(f => f.citation).join('; and ')
    : (other ? `the requirements of ${other}` : 'the applicable regulatory framework');

  // Where several frameworks apply, cite each so the reviewer can see the basis for the row.
  const cite = (key) => {
    const found = frameworks.map(f => f.requirementCitations?.[key]).filter(Boolean);
    return found.length ? found.join(' · ') : '';
  };

  return {
    configured: !unconfigured,
    frameworks,
    labels,
    shorts,
    frameworkLine: labels.length ? labels.join(' and ') : 'Framework not configured',
    citation,
    cite,
    has: (instrument) => instruments.has(instrument),
    instruments
  };
}

export default frameworkContext;
